/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimateSharedLayout, motion } from "framer-motion";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  SelectedAllowedCategoriesAtom,
  SelectedCategoriesAtom,
} from "../../../../atoms/SelectedCategoriesAtom";
import { UpdateCategoryIRoute } from "../../../../generated/Api";
import { notSelect } from "../../../../styles/general";
import { useModal } from "../../../Modal/Modal";
import StyledButton from "../../../StyledButton";
import TableRowCategories from "./TableRowCategories";

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: scroll;
  padding: 20px;
  max-height: 70vh;

  > p {
    margin: 0px 10px 20px 10px;
    font-size: 18px;
    font-weight: 600;
    color: #2f2f2f;
    text-align: center;
  }
`;

const TableCard = styled.div`
  width: 100%;

  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  display: grid;

  grid-template-columns: 1fr 0.5fr;
  grid-column-gap: 24px;
  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1010px) {
    width: fit-content;
  }
`;

const TableHead = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  font-weight: 600;
  cursor: default;
  position: relative;
  border-radius: 4px;
  ${notSelect}
`;

const FlexRow = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const WarningBanner = styled.div`
  width: 660px;
  background-color: #ffd8d8;
  color: #333333;
  padding: 10px 20px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
`;

const HorizontalDivider = styled.div`
  margin-left: -25px;
  width: calc(100% + 50px);
  border-top: 1px solid #eaeaea;
`;

const FooterContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #333333;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

interface Props {
  allSelectedCategories: UpdateCategoryIRoute[];
  deleteCategories: (categoriesIds: string[]) => void;
  categoriesReadyToBeDeleted: UpdateCategoryIRoute[];
  categoriesRestricted: UpdateCategoryIRoute[];
}

export default function BatchDeleteCategories(props: Props) {
  const {
    allSelectedCategories,
    deleteCategories,
    categoriesReadyToBeDeleted,
    categoriesRestricted,
  } = props;

  const modal = useModal();

  const selectedCategories = useRecoilValue(SelectedCategoriesAtom);
  const [selectedAllowedCategories, setSelectedAllowedCategories] =
    useRecoilState(SelectedAllowedCategoriesAtom);

  function handleCheck(id: string) {
    setSelectedAllowedCategories((elems) => {
      if (elems.includes(id)) {
        return elems.filter((check) => check !== id);
      }
      return [...elems, id];
    });
  }

  return (
    <div>
      <TableContainer>
        <p>Delete Categories</p>
        {!!categoriesRestricted.length && (
          <WarningBanner>{`${categoriesRestricted.length} out of ${allSelectedCategories.length} selected categories have tags that are being used in campaigns/forms and cannot be deleted.`}</WarningBanner>
        )}
        <TableCard>
          <TableHead>
            <FlexRow>CATEGORIES</FlexRow>
          </TableHead>
          <TableHead>
            <FlexRow>TAGS</FlexRow>
          </TableHead>
        </TableCard>
        <AnimateSharedLayout>
          {allSelectedCategories.map((rowWithoutLowercase) => {
            return (
              <TableRowCategories
                key={rowWithoutLowercase._id}
                row={rowWithoutLowercase}
                categoriesReadyToBeDeleted={categoriesReadyToBeDeleted}
                categoriesRestricted={categoriesRestricted}
                isChecked={selectedAllowedCategories.includes(
                  rowWithoutLowercase._id
                )}
                handleCheck={() => handleCheck(rowWithoutLowercase._id)}
              />
            );
          })}
        </AnimateSharedLayout>
      </TableContainer>
      <HorizontalDivider />
      <FooterContainer>
        <span>
          This action cannot be reversed. Are you sure you want to delete these
          categories permanently?
        </span>
        <ButtonsContainer>
          <StyledButton
            inverted
            css={css`
              width: 100px;
            `}
            onClick={() => modal.popModal()}
          >
            No
          </StyledButton>
          <StyledButton
            css={css`
              margin-left: 10px;
              width: 100px;
            `}
            onClick={() => deleteCategories(selectedAllowedCategories)}
          >
            Yes
          </StyledButton>
        </ButtonsContainer>
      </FooterContainer>
    </div>
  );
}
